import {
  forwardRef,
  createContext,
  Dispatch,
  SetStateAction,
  useState,
  useRef,
  useEffect,
  useId,
  ComponentPropsWithoutRef,
  KeyboardEvent,
  useMemo,
  useCallback,
} from "react";
import { TabButton } from "./TabButton";
import { TabList } from "./TabList";
import { TabPanel } from "./TabPanel";
import styles from "./Tab.module.scss";
import cx from "classnames";
import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil, useMergeRefs } from "../../hooks";
import { motion, AnimatePresence } from "motion/react";

/**
 * Props for the Tab component
 * @extends Omit<ComponentPropsWithoutRef<"div">, "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag">
 * @extends LayoutUtilProps
 */
export type TabProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag"
> &
  LayoutUtilProps & {
    /**
     * The index of the tab that should be active by default.
     */
    defaultIndex: number;
    /**
     * Whether the tabs should fill the available width.
     * @default false
     */
    fill?: boolean;
  };

type TabContextProps = {
  current: number; // id of tabpanel
  setCurrent?: Dispatch<SetStateAction<number>>;
  focus: number; // id of tabpanel
  setFocus?: Dispatch<SetStateAction<number>>;
  items?: NodeListOf<HTMLElement>;
  uid?: string;
  onKeyDownHandler?: (e: KeyboardEvent<HTMLButtonElement>) => void;
  gotoNext?: (refocus?: boolean) => void;
  goToPrev?: (refocus?: boolean) => void;
  dir?: string;
};

const initialValue = {
  current: 0,
  focus: 0,
};

export const TabContext = createContext<TabContextProps>(initialValue);
const TabElement = forwardRef<HTMLDivElement, TabProps>((props, ref) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const { defaultIndex, className, style, fill, ...rest } = componentProps;
  const tabRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<NodeListOf<HTMLElement>>();
  const [current, setCurrent] = useState<number>(defaultIndex ?? 0);
  const [focus, setFocus] = useState<number>(defaultIndex ?? 0);
  const [dir, setDir] = useState("ltr");
  const tabClassNames = cx(styles["tab"], className, {
    [styles["fill"]]: fill,
  });
  const uid = useId();

  useEffect(() => {
    setItems(tabRef.current?.querySelectorAll("[role=tab]:not([disabled])"));
  }, [tabRef]);

  useEffect(() => {
    if (tabRef.current?.closest("[dir]")?.getAttribute("dir") === "rtl") {
      setDir("rtl");
    } else {
      setDir("ltr");
    }
  }, []);

  useEffect(() => {
    if (!tabRef.current) return;

    const mutationCallback = () => {
      setItems(tabRef.current?.querySelectorAll("[role=tab]:not([disabled])"));
    };

    const observer = new MutationObserver(mutationCallback);
    observer.observe(tabRef.current, {
      childList: true, // Observe changes to child elements
      subtree: true, // Observe changes to descendants of the target
      attributes: true, // Observe changes to attributes
      characterData: true, // Observe changes to text content
    });
    return () => {
      observer.disconnect();
    };
  }, []);

  const styleCombined = {
    ...style,
    ...layoutStyles,
  };

  // scroll behavior of the focus tab item
  const focusScroller = (node: HTMLElement | undefined) => {
    node?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  };

  const gotoNext = useCallback(
    (_refocus = true) => {
      if (focus + 1 === items?.length) {
        setFocus?.(0);
        items?.[0].focus();
        focusScroller(items?.[0]);
        return;
      }
      setFocus?.(focus + 1);
      items?.[focus + 1].focus();
      focusScroller(items?.[focus + 1]);
    },
    [focus, items],
  );

  const goToPrev = useCallback(
    (_refocus = true) => {
      if (focus === 0) {
        const lastItemIndex = items ? items?.length - 1 : 0;
        setFocus?.(lastItemIndex);
        items?.[lastItemIndex].focus();
        focusScroller(items?.[lastItemIndex]);
        return;
      }
      setFocus?.(focus - 1);
      items?.[focus - 1].focus();
      focusScroller(items?.[focus - 1]);
    },
    [focus, items],
  );

  const onKeyDownHandler = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      switch (e.code) {
        case "Enter":
        case "Space":
          setCurrent?.(focus);
          break;
        case "ArrowRight":
          e.preventDefault();
          if (dir === "ltr") {
            gotoNext();
          } else {
            goToPrev();
          }
          break;
        case "ArrowLeft":
          e.preventDefault();
          if (dir === "ltr") {
            goToPrev();
          } else {
            gotoNext();
          }
          break;
        default:
          break;
      }
    },
    [dir, focus, goToPrev, gotoNext],
  );

  const value = useMemo(
    () => ({
      current,
      setCurrent,
      focus,
      setFocus,
      items,
      uid,
      onKeyDownHandler,
      gotoNext,
      goToPrev,
      dir,
    }),
    [current, focus, goToPrev, gotoNext, items, onKeyDownHandler, uid, dir],
  );

  return (
    <TabContext.Provider value={value}>
      <AnimatePresence initial={false}>
        <motion.div
          transition={{ duration: 0 }}
          layout
          className={tabClassNames}
          data-anv="tab"
          style={styleCombined}
          {...rest}
          ref={useMergeRefs([tabRef, ref])}
        />
      </AnimatePresence>
    </TabContext.Provider>
  );
});

TabElement.displayName = "Tab";

/**
 * Tab component for organizing content into multiple sections with navigation.
 *
 * Features:
 * - Keyboard navigation support (arrow keys, Enter, Space)
 * - Accessible with proper ARIA roles and attributes
 * - Smooth animations and transitions
 * - RTL language support
 * - Automatic focus management
 * - Scroll behavior for overflow tabs
 * - Compound component pattern with Tab.Button, Tab.List, and Tab.Panel
 * - Supports layout utilities for positioning and spacing
 *
 * @example
 * <Tab defaultIndex={0}>
 *   <Tab.List>
 *     <Tab.Button id="tab1" controls="panel1">First Tab</Tab.Button>
 *     <Tab.Button id="tab2" controls="panel2">Second Tab</Tab.Button>
 *   </Tab.List>
 *   <Tab.Panel id="panel1">Content for first tab</Tab.Panel>
 *   <Tab.Panel id="panel2">Content for second tab</Tab.Panel>
 * </Tab>
 */
export const Tab = Object.assign(TabElement, {
  /**
   * TabButton component for individual tab navigation buttons.
   *
   * Features:
   * - Automatic ARIA attributes for accessibility
   * - Visual indicator for active state
   * - Keyboard navigation support
   * - Smooth animations with reduced motion support
   * - Proper focus management
   * - RTL language support
   *
   * @example
   * <Tab.Button id="tab1" controls="panel1" onClick={(e, index) => console.log('Tab clicked:', index)}>
   *   First Tab
   * </Tab.Button>
   */
  Button: TabButton,
  /**
   * TabList component for containing and managing tab buttons.
   *
   * Features:
   * - Horizontal scrolling for overflow tabs
   * - Navigation arrows for overflow content
   * - RTL language support
   * - Automatic overflow detection
   * - Proper ARIA roles for accessibility
   * - Smooth scrolling behavior
   * - Visual indicator positioning
   *
   * @example
   * <Tab.List>
   *   <Tab.Button id="tab1" controls="panel1">First Tab</Tab.Button>
   *   <Tab.Button id="tab2" controls="panel2">Second Tab</Tab.Button>
   *   <Tab.Button id="tab3" controls="panel3">Third Tab</Tab.Button>
   * </Tab.List>
   */
  List: TabList,
  /**
   * TabPanel component for displaying tab content.
   *
   * Features:
   * - Automatic visibility based on active tab
   * - Smooth fade animations
   * - Proper ARIA attributes for accessibility
   * - Automatic association with tab buttons
   * - Focus management
   * - Layout animations
   *
   * @example
   * <Tab.Panel id="panel1">
   *   <h2>First Tab Content</h2>
   *   <p>This is the content for the first tab.</p>
   * </Tab.Panel>
   */
  Panel: TabPanel,
});
